// Today's early clock-out request, sitting where the Clock Out button is.
//
// Without this line the gate is invisible between pressing Clock Out and being
// approved: the punch was refused, a request went somewhere, and the page looked
// exactly as it did before. It answers the three questions that follow in order
// — is it sent, who has it, and can I leave now.

import { Check, Clock, X } from 'lucide-react';
import type { EarlyExitRequestRow } from '../../api/attendance';

interface Props {
  request: EarlyExitRequestRow | null;
  /** Withdraw a pending request (they decided to stay). */
  onWithdraw?: (request: EarlyExitRequestRow) => void;
  withdrawing?: boolean;
}

export default function EarlyExitBanner({ request, onWithdraw, withdrawing }: Props) {
  // A spent approval is history: the punch it authorised has already happened.
  if (!request || (request.status === 'APPROVED' && request.used_at)) return null;
  if (request.status === 'CANCELLED') return null;

  if (request.status === 'PENDING') {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        <span>
          Early clock-out{' '}
          {request.approver_name ? (
            <>waiting with <span className="font-semibold">{request.approver_name}</span></>
          ) : (
            'waiting for approval'
          )}
          .
        </span>
        {onWithdraw && (
          <button
            type="button"
            onClick={() => onWithdraw(request)}
            disabled={withdrawing}
            className="font-medium underline underline-offset-2 hover:text-amber-950 disabled:opacity-60"
          >
            {withdrawing ? 'Withdrawing…' : 'Withdraw'}
          </button>
        )}
      </div>
    );
  }

  if (request.status === 'APPROVED') {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-green-200 bg-green-50 px-2.5 py-1.5 text-xs text-green-900">
        <Check className="h-3.5 w-3.5 shrink-0" />
        <span>
          Early clock-out approved
          {request.approver_name ? ` by ${request.approver_name}` : ''} — covers
          this one punch.
        </span>
        {request.decision_note && (
          <span className="text-green-800">“{request.decision_note}”</span>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs text-red-900">
      <X className="h-3.5 w-3.5 shrink-0" />
      <span>
        Early clock-out declined
        {request.approver_name ? ` by ${request.approver_name}` : ''}.
      </span>
      {request.decision_note && (
        <span className="text-red-800">“{request.decision_note}”</span>
      )}
    </div>
  );
}
